'use client';

import React from 'react';
import { Clock, DollarSign, Target } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';

const Benefits: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isVisible } = useIntersectionObserver();

  const benefits = [
    {
      icon: <Clock className="w-8 h-8 text-primary-500" />,
      title: t('pageContent.home.benefits.items.time.title'),
      description: t('pageContent.home.benefits.items.time.description'),
    },
    {
      icon: <Target className="w-8 h-8 text-primary-500" />,
      title: t('pageContent.home.benefits.items.results.title'),
      description: t('pageContent.home.benefits.items.results.description'),
    },
    {
      icon: <DollarSign className="w-8 h-8 text-primary-500" />,
      title: t('pageContent.home.benefits.items.cost.title'),
      description: t('pageContent.home.benefits.items.cost.description'),
    },
  ];

  return (
    <section id="benefits" className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      <div className="container relative z-10">
        <div ref={ref} className={`text-center mb-16 fade-in-section ${isVisible ? 'is-visible' : ''}`}>
          <div className="inline-block bg-primary-50 rounded-full px-6 py-2 text-primary-600 font-medium text-sm mb-4 animate-pulse-shadow">
            {t('pageContent.home.benefits.tagline')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">{t('pageContent.home.benefits.title')}</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => (
            <div
              key={index}
              className={`flex flex-col items-center text-center p-8 rounded-xl bg-white/80 backdrop-blur-sm border border-gray-100 hover:border-primary-200 transition-all duration-300 hover-lift fade-in-section ${isVisible ? 'is-visible' : ''}`}
              style={{ transitionDelay: `${index * 0.2}s` }}
            >
              <div className="rounded-full bg-primary-50 w-16 h-16 flex items-center justify-center mb-6 hover-rotate">
                {benefit.icon}
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-3">{benefit.title}</h3>
              <p className="text-gray-600">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-secondary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>
    </section>
  );
};

export { Benefits };